import { createAsyncThunk, createSlice, PayloadAction } from '@reduxjs/toolkit';

import {
  AssignedManager,
  Email,
  LenderIdentificationStatus,
  Money,
  PersonalData,
  Phone,
  Profile,
  Role,
  UserGender,
  UserPlan,
  UserStatus,
} from 'api-client';

import { FeeSettings } from '../api-client/models/project/common/FeeSettings';
import { Deal } from '../api-client/models/user/Deal';
import { createProfileManager } from '../di';
import { Nullable } from '../utils';

const toPlainMoney = (money: Money): Money => ({
  amount: money.amount,
  currencyCode: money.currencyCode,
});

export type ProfileState = {
  uuid?: string;
  role: Role;
  status: UserStatus;
  username: string;
  name: string;
  gender: Nullable<UserGender>;
  balance: Money;
  reservedFunds: Money;
  paymentCode?: string;
  email: Nullable<Email>;
  phone: Phone;
  phoneVerified: boolean;
  lenderVerified: boolean;
  personalData: Nullable<PersonalData>;
  lenderCanDeposits: boolean;
  lenderStatus: LenderIdentificationStatus;
  hasIdentificationDraft: boolean;
  identificationDate: string;
  isEntrepreneur: boolean;
  isLegalEntity: boolean;
  agentCode: Nullable<string>;
  hasTransactions: boolean;
  newNotificationCount: number;
  premiumAvailable: Nullable<boolean>;
  premiumEnabled: Nullable<boolean>;
  referralEnabled: Nullable<boolean>;
  referrer: Nullable<string>;
  assignedManager: Nullable<AssignedManager>;
  deal: Nullable<Deal>;
  investmentFee: Nullable<FeeSettings>;
  specialAIEnabled: Nullable<boolean>;
  identificationNotes: Nullable<string>;
  userPlan: Nullable<UserPlan>;
  capitalProtectedInvestmentsCount: Nullable<number>;
  passportExpired: boolean;
};

const initialState: ProfileState = {
  uuid: undefined,
  role: Role.LENDER,
  status: UserStatus.UNKNOWN,
  username: '',
  name: '',
  gender: null,
  balance: { amount: 0, currencyCode: '' },
  reservedFunds: { amount: 0, currencyCode: '' },
  paymentCode: '',
  email: null,
  phone: new Phone('', ''),
  phoneVerified: false,
  lenderVerified: false,
  personalData: null,
  lenderCanDeposits: false,
  lenderStatus: LenderIdentificationStatus.NOT_VERIFIED,
  hasIdentificationDraft: false,
  identificationDate: '',
  isEntrepreneur: false,
  isLegalEntity: false,
  agentCode: null,
  hasTransactions: false,
  newNotificationCount: 0,
  premiumAvailable: null,
  premiumEnabled: null,
  referralEnabled: null,
  assignedManager: null,
  deal: null,
  investmentFee: null,
  referrer: null,
  specialAIEnabled: null,
  identificationNotes: null,
  userPlan: null,
  capitalProtectedInvestmentsCount: null,
  passportExpired: false,
};

export const refreshProfile = createAsyncThunk('profile/refresh', async (_) => {
  const manager = await createProfileManager();
  return await manager.get();
});

export const profileSlice = createSlice({
  name: 'profile',
  initialState,
  reducers: {
    set: (state: ProfileState, { payload }: PayloadAction<Profile>) => {
      state.uuid = payload.uuid;
      state.role = payload.role;
      state.status = payload.status;
      state.username = payload.username;
      state.name = payload.name ?? null;
      state.gender = payload.gender ?? null;
      state.balance = toPlainMoney(payload.balance);
      state.reservedFunds = toPlainMoney(payload.reservedFunds);
      state.paymentCode = payload.paymentCode;
      state.email = payload.email;
      state.phone = payload.phone;
      state.phoneVerified = payload.phoneVerified;
      state.lenderVerified = payload.lender!.verified;
      state.personalData = payload.lender!.personalData ?? null;
      state.isEntrepreneur = state.status === UserStatus.ENTREPRENEUR;
      state.isLegalEntity = state.status === UserStatus.LEGAL_ENTITY;
      state.hasIdentificationDraft = payload.lender!.hasIdentificationDraft;
      state.lenderStatus = payload.lender!.identificationStatus;
      state.identificationDate = payload.lender!.identificationDate ?? '';
      state.agentCode = payload.lender!.agentCode ?? null;
      state.hasTransactions = payload.lender!.hasTransactions;
      state.newNotificationCount = payload.newNotificationCount;
      state.premiumAvailable = payload.lender!.premiumAvailable ?? null;
      state.specialAIEnabled = payload.lender!.specialAIEnabled ?? null;
      state.premiumEnabled = payload.lender!.premiumEnabled ?? null;
      state.referralEnabled = payload.lender!.referralEnabled ?? null;
      state.assignedManager = payload.assignedManager ?? null;
      state.deal = payload.deal ?? null;
      state.referrer = payload.lender!.referrer ?? null;
      state.identificationNotes = payload.lender!.identificationNotes ?? null;
      state.userPlan = payload.lender!.userPlan ?? null;
      state.capitalProtectedInvestmentsCount =
        payload.lender!.capitalProtectedInvestmentsCount ?? null;
      state.passportExpired = payload.lender!.passportExpired ?? false;
    },
    subtractBalance: (state: ProfileState, { payload }: PayloadAction<number>) => {
      if (state.balance) {
        state.balance = {
          amount: state.balance.amount - payload,
          currencyCode: state.balance.currencyCode,
        };
      }
    },
    setInvestmentFee: (state: ProfileState, { payload }: PayloadAction<FeeSettings>) => {
      state.investmentFee = payload;
    },
    setIdentificationDraft: (state: ProfileState, { payload }: PayloadAction<boolean>) => {
      state.hasIdentificationDraft = payload;
    },
    setReferralEnabled: (state: ProfileState, { payload }: PayloadAction<boolean>) => {
      state.referralEnabled = payload;
    },
    addAgentCode: (state: ProfileState, { payload }: PayloadAction<string>) => {
      state.agentCode = payload;
    },
    changeGender: (state: ProfileState, { payload }: PayloadAction<UserGender>) => {
      state.gender = payload;
    },
    changeNotificationCount: (state: ProfileState, { payload }: PayloadAction<number>) => {
      state.newNotificationCount = payload;
    },
    clear: (state: ProfileState) => {
      delete state.uuid;
      delete state.paymentCode;
    },
  },
  extraReducers: (builder) => {
    builder.addCase(refreshProfile.fulfilled, (state, { payload }) => {
      return {
        ...state,
        ...payload,
      };
    });
  },
});

export const {
  set,
  setIdentificationDraft,
  addAgentCode,
  subtractBalance,
  changeGender,
  changeNotificationCount,
  clear,
  setReferralEnabled,
  setInvestmentFee,
} = profileSlice.actions;

export default profileSlice.reducer;
